import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  FormControlLabel,
  Switch,
  Typography,
  Grid,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import Input from "themeComponents/Input";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { courseApi, userApi } from "configs/api";
import Button from "themeComponents/Button";
import Box from "themeComponents/Box";
import FormikInput from "components/formikInput";
import { AppContext } from "context/appContext";
import { useContext } from "react";
import useAuth from "hooks/useAuth";
import { CheckCircle, Cancel } from "@mui/icons-material";

const isUnlimitedSeats = (value?: number | null): boolean => Number(value) === -1;
const formatSeatsLabel = (value?: number | null): string => (isUnlimitedSeats(value) ? "Unlimited" : String(Number(value) || 0));

interface Company {
  _id: string;
  name: string;
  email: string;
  companyName?: string;
}

interface Employee {
  _id: string;
  name: string;
  email: string;
  role: string;
}

interface Branch {
  _id: string;
  name: string;
  email?: string;
}

interface CourseAssignment {
  id: string;
  courseID: string;
  userID: string;
  quota: number;
  quotaUsed: number;
  isFreeAccess: boolean;
  expiryDate?: string;
  status: string;
}

interface CourseAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  courseID: string;
  courseTitle: string;
  courseCompanyID?: string;
  courseAddedByUserID?: string;
  courseInstructorID?: string;
  isPaidCourse?: boolean;
  availablePurchasedSeats?: number;
  onSuccess?: () => void;
}

function CourseAssignmentDialog({
  open,
  onClose,
  courseID,
  courseTitle,
  courseCompanyID,
  courseAddedByUserID,
  courseInstructorID,
  isPaidCourse = false,
  availablePurchasedSeats,
  onSuccess,
}: CourseAssignmentDialogProps): React.JSX.Element {
  const { showAlert } = useContext(AppContext);
  const { user, isAdmin, isCompany, isCompanyAdmin, isFellowAdmin, isCompanyEmployee, isManager, isHR, isRestrictedEmployee } = useAuth();
  const normalizeId = (value: any): string => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object") {
      return value._id || value.id || value.value || "";
    }
    return String(value);
  };

  const roleLower = String(user?.role || "").toLowerCase();
  const positionLower = String(user?.position || "").toLowerCase();
  const isManagerOrHRResolved =
    isManager ||
    isHR ||
    roleLower === "manager" ||
    roleLower === "hr" ||
    positionLower === "manager" ||
    positionLower === "hr";

  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [assignmentType, setAssignmentType] = useState<'company' | 'employee' | 'branch'>('company');
  const [existingAssignment, setExistingAssignment] = useState<CourseAssignment | null>(null);
  const [quotaAvailable, setQuotaAvailable] = useState<number>(0);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingAssignment, setLoadingAssignment] = useState(false);
  const [courseAssignments, setCourseAssignments] = useState<CourseAssignment[]>([]);
  const [branchSeatsAvailable, setBranchSeatsAvailable] = useState<number>(0);
  const [courseOwnerRole, setCourseOwnerRole] = useState<string>("");

  const isManagerOrHR = isManagerOrHRResolved;
  const isSuperAdmin = isAdmin;
  const isCompanyScopeAdmin = isCompanyAdmin || isFellowAdmin;
  const isCompanyTargetAssigner = isSuperAdmin || isCompanyScopeAdmin;
  const hasBranch = Boolean(user?.branchID);
  const canAssignToBranch = isCompany || (isManagerOrHR && !hasBranch);
  const restrictedOwnerRolesForSuperAdmin = new Set(["creator", "companyadmin", "fellowadmin"]);
  const hideSuperAdminCompanyControls =
    isSuperAdmin &&
    isCompanyTargetAssigner &&
    isPaidCourse &&
    restrictedOwnerRolesForSuperAdmin.has(courseOwnerRole);
  const hasForwardingQuotaConstraint = Boolean(isCompany && existingAssignment?.isFreeAccess);

  /** Active assignment target IDs (company user id, branch id, employee id, etc.) */
  const activeAssignmentUserIds = useMemo(() => {
    const ids = new Set<string>();
    for (const a of courseAssignments) {
      if (a.status === "active" && a.userID) ids.add(String(a.userID));
    }
    return ids;
  }, [courseAssignments]);

  /** Employees who already have a direct (per-user) assignment — disable in employee picker only */
  const assignedEmployees = useMemo(
    () => employees.filter((emp) => activeAssignmentUserIds.has(String(emp._id))),
    [employees, activeAssignmentUserIds]
  );

  const isCompanyOptionAssigned = (c: Company) => activeAssignmentUserIds.has(String(c._id));
  const isBranchOptionAssigned = (b: Branch) => activeAssignmentUserIds.has(String(b._id));

  useEffect(() => {
    if (!open || !courseID) {
      setCourseAssignments([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoadingAssignment(true);
      try {
        const response = await courseApi.get(`/api/courses/assignments/course/${courseID}`);
        if (!cancelled && response.data?.status && Array.isArray(response.data.data)) {
          setCourseAssignments(response.data.data);
        } else if (!cancelled) {
          setCourseAssignments([]);
        }
      } catch {
        if (!cancelled) setCourseAssignments([]);
      } finally {
        if (!cancelled) setLoadingAssignment(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [open, courseID]);

  useEffect(() => {
    if (!open) {
      setExistingAssignment(null);
      setQuotaAvailable(0);
      return;
    }
    if (!isCompany || !user) {
      setExistingAssignment(null);
      setQuotaAvailable(0);
      return;
    }
    const uid = normalizeId(user?.userId || user?._id);
    const assignment = courseAssignments.find((a) => a.status === "active" && uid && String(a.userID) === uid);
    if (assignment) {
      setExistingAssignment(assignment);
      setQuotaAvailable(isUnlimitedSeats(assignment.quota) ? -1 : assignment.quota - assignment.quotaUsed);
    } else {
      setExistingAssignment(null);
      setQuotaAvailable(0);
    }
  }, [open, isCompany, user, courseAssignments]);

  const fetchCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    try {
      const companyResponse = await userApi.get("/api/users", { params: { role: "company", limit: 200 } });
      const companies1 = companyResponse.data.status ? (companyResponse.data.data?.data || []) : [];
      const all: Company[] = companies1;

      let filtered = courseCompanyID ? all.filter((c) => c._id !== courseCompanyID) : all;

      if (isCompanyScopeAdmin) {
        let allowedCompanyIds: string[] = user?.assignedCompanies || [];

        if (isFellowAdmin && allowedCompanyIds.length === 0) {
          let parentAdmin: any = null;
          if (user?.adminID) {
            try {
              const parentRes = await userApi.get(`/api/users/${user.adminID}`);
              parentAdmin = parentRes.data?.data || null;
            } catch {
              parentAdmin = null;
            }
          }

          if (!parentAdmin && user?.adminEmail) {
            try {
              const adminsRes = await userApi.get("/api/users", { params: { role: "companyadmin", limit: 200 } });
              const admins = adminsRes.data?.status ? adminsRes.data.data?.data || [] : [];
              const normalizedAdminEmail = String(user.adminEmail).toLowerCase();
              parentAdmin = admins.find((a: any) => String(a.email || "").toLowerCase() === normalizedAdminEmail) || null;
            } catch {
              parentAdmin = null;
            }
          }

          allowedCompanyIds = parentAdmin?.assignedCompanies || parentAdmin?.assignedcompanies || [];
        }

        filtered = allowedCompanyIds.length > 0
          ? filtered.filter((c) => allowedCompanyIds.includes(c._id))
          : [];
      }

      setCompanies(filtered);
    } catch (error: any) {
      showAlert(error.response?.data?.message || "Failed to fetch companies", "error", "Error");
    } finally {
      setLoadingCompanies(false);
    }
  }, [courseCompanyID, showAlert, isCompanyScopeAdmin, isFellowAdmin, user?.assignedCompanies, user?.adminID, user?.adminEmail]);

  const fetchEmployees = useCallback(async () => {
    const companyID = user?.userId || user?._id;
    if (!companyID) return;
    setLoadingEmployees(true);
    try {
      const employeeResponse = await userApi.get("/api/users", { params: { companyID, role: "employee", limit: 200 } });
      const employees1 = employeeResponse.data.status ? (employeeResponse.data.data?.data || []) : [];
      setEmployees(employees1);
    } catch (error: any) {
      showAlert(error.response?.data?.message || "Failed to fetch employees", "error", "Error");
    } finally {
      setLoadingEmployees(false);
    }
  }, [user?.userId, user?._id, showAlert]);

  const fetchManagerEmployees = useCallback(async () => {
    const requestUserId = user?.userId || user?._id;
    if (!requestUserId) return;
    setLoadingEmployees(true);
    try {
      const assignedCompanies = [
        ...(Array.isArray(user?.assignedCompanies) ? user.assignedCompanies : []),
        ...(Array.isArray(user?.assignedcompanies) ? user.assignedcompanies : []),
      ];

      const firstAssignedCompanyID = assignedCompanies
        .map((entry: any) => normalizeId(entry))
        .find((id: string) => Boolean(id));

      const resolvedCompanyID =
        normalizeId(user?.companyID) ||
        normalizeId((user as any)?.CompanyID) ||
        normalizeId((user as any)?.companyId) ||
        firstAssignedCompanyID ||
        "";

      if (!resolvedCompanyID) {
        setEmployees([]);
        return;
      }

      const params: any = { companyID: resolvedCompanyID, role: "employee", limit: 200 };
      if (hasBranch && user.branchID) params.branchID = user.branchID;

      const empResponse = await userApi.get("/api/users", { params });
      let employees = empResponse.data.status ? (empResponse.data.data?.data || []) : [];

      // Fallback: if strict branch filter returns nothing, fall back to company employees.
      if (hasBranch && employees.length === 0) {
        const fallbackResponse = await userApi.get("/api/users", {
          params: { companyID: resolvedCompanyID, role: "employee", limit: 200 },
        });
        employees = fallbackResponse.data.status ? (fallbackResponse.data.data?.data || []) : [];
      }

      setEmployees(employees);
    } catch (error: any) {
      showAlert(error.response?.data?.message || "Failed to fetch employees", "error", "Error");
    } finally {
      setLoadingEmployees(false);
    }
  }, [user?.userId, user?._id, user?.companyID, user?.assignedCompanies, user?.assignedcompanies, user?.branchID, hasBranch, showAlert]);

  const fetchBranches = useCallback(async () => {
    const companyID = user?.companyID || user?.userId || user?._id;
    if (!companyID) return;
    setLoadingBranches(true);
    try {
      const response = await userApi.get("/api/users/branches", { params: { companyID } });
      const rows = response.data?.status ? response.data.data?.data || [] : [];
      setBranches(
        rows.map((b: any) => ({
          _id: b._id,
          name: b.name || b._id,
          email: b.email,
        }))
      );
    } catch (error: any) {
      showAlert(error.response?.data?.message || "Failed to fetch branches", "error", "Error");
    } finally {
      setLoadingBranches(false);
    }
  }, [user?.companyID, user?.userId, user?._id, showAlert]);

  useEffect(() => {
    if (!open) return;

    if (isCompanyTargetAssigner) {
      fetchCompanies();
      setAssignmentType('company');
    } else if (isCompany) {
      setAssignmentType('employee');
      fetchEmployees();
    } else if (isManagerOrHR) {
      setAssignmentType('employee');
      fetchManagerEmployees();
    }
  }, [open, isCompanyTargetAssigner, isCompany, isManagerOrHR, fetchCompanies, fetchEmployees, fetchManagerEmployees]);

  useEffect(() => {
    const resolveCourseOwnerRole = async () => {
      if (!open || !isSuperAdmin) {
        setCourseOwnerRole("");
        return;
      }

      const ownerUserId = String(courseAddedByUserID || courseInstructorID || "").trim();
      if (!ownerUserId) {
        setCourseOwnerRole("");
        return;
      }

      try {
        const response = await userApi.get(`/api/users/${ownerUserId}`);
        const role = String(response?.data?.data?.role || "").toLowerCase();
        setCourseOwnerRole(role);
      } catch {
        setCourseOwnerRole("");
      }
    };

    void resolveCourseOwnerRole();
  }, [open, isSuperAdmin, courseAddedByUserID, courseInstructorID]);

  useEffect(() => {
    if (open && canAssignToBranch && assignmentType === 'branch') {
      fetchBranches();
    }
  }, [assignmentType, open, canAssignToBranch, fetchBranches]);

  /** Paid branch assignment: seat cap matches course detail purchaseInfo (course service). */
  useEffect(() => {
    if (!open || !canAssignToBranch || assignmentType !== 'branch' || !isPaidCourse) {
      return;
    }

    const fetchPurchasedSeats = async () => {
      try {
        const response = await courseApi.get(`/api/courses/${courseID}/assignment-quota`, { skipAutoLogout: true } as any);
        const data = response?.data?.data;
        const rawAvail = data?.quotaAvailable;
        if (rawAvail === -1 || rawAvail === "-1" || Number(rawAvail) === -1) {
          setBranchSeatsAvailable(-1);
          return;
        }
        const available = Number(rawAvail ?? 0);
        setBranchSeatsAvailable(Number.isFinite(available) ? available : 0);
      } catch {
        const fallback =
          availablePurchasedSeats !== undefined && availablePurchasedSeats !== null
            ? Number(availablePurchasedSeats)
            : 0;
        setBranchSeatsAvailable(Number.isFinite(fallback) ? fallback : 0);
      }
    };

    void fetchPurchasedSeats();
  }, [open, canAssignToBranch, assignmentType, isPaidCourse, courseID]);

  const handleSubmit = async (values: any) => {
    if (isCompanyTargetAssigner && !selectedCompany) {
      showAlert("Please select a company", "error", "Validation Error");
      return;
    }
    if ((isCompany || isManagerOrHR) && assignmentType === 'employee' && !selectedEmployee) {
      showAlert("Please select an employee", "error", "Validation Error");
      return;
    }
    if (canAssignToBranch && assignmentType === 'branch' && !selectedBranch) {
      showAlert("Please select a branch", "error", "Validation Error");
      return;
    }
    if (canAssignToBranch && assignmentType === 'branch' && isPaidCourse) {
      const seats = Number(values.quota);
      if (!Number.isInteger(seats) || seats <= 0) {
        showAlert("Seats is required and must be greater than 0.", "error", "Validation Error");
        return;
      }
      if (!isUnlimitedSeats(branchSeatsAvailable) && seats > branchSeatsAvailable) {
        showAlert(`Seats must be less than or equal to available seats (${formatSeatsLabel(branchSeatsAvailable)}).`, "error", "Validation Error");
        return;
      }
    }
    if (isCompany && assignmentType === 'employee' && hasForwardingQuotaConstraint && !isUnlimitedSeats(quotaAvailable) && quotaAvailable <= 0) {
      showAlert("No quota available. All assigned quota has been used.", "error", "Quota Exceeded");
      return;
    }

    setLoading(true);
    try {
      let targetUserID: string;
      if (isCompanyTargetAssigner) {
        targetUserID = selectedCompany!._id;
      } else if (assignmentType === 'branch') {
        targetUserID = selectedBranch!._id;
      } else {
        targetUserID = selectedEmployee!._id;
      }

      const targetType = isCompanyTargetAssigner
        ? "company"
        : assignmentType === 'branch'
          ? "branch"
          : "user";

      if ((isCompany || isManagerOrHR) && assignmentType === 'employee' && selectedEmployee) {
        const isAlreadyAssigned = assignedEmployees.some((emp) => String(emp._id) === String(selectedEmployee._id));
        if (isAlreadyAssigned) {
          showAlert("This course is already assigned to this employee", "error", "Already Assigned");
          setLoading(false);
          return;
        }
      }

      const response = await courseApi.post("/api/courses/assign", {
        courseID,
        userID: targetUserID,
        targetType,
        quota: assignmentType === 'branch' ? Number(values.quota) : (isCompanyTargetAssigner ? (hideSuperAdminCompanyControls ? 0 : Number(values.quota)) : 1),
        isFreeAccess: hideSuperAdminCompanyControls ? false : (values.isFreeAccess || false),
        expiryDate:
          hideSuperAdminCompanyControls || !values.isFreeAccess ? undefined : values.expiryDate || undefined,
      });

      if (response.data.status) {
        showAlert("Course assigned successfully!", "success", "Success");
        onSuccess?.();
        handleClose();
      } else {
        showAlert(response.data.message || "Failed to assign course", "error", "Error");
      }
    } catch (error: any) {
      showAlert(error.response?.data?.message || "Failed to assign course", "error", "Error");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedCompany(null);
    setSelectedEmployee(null);
    setSelectedBranch(null);
    setExistingAssignment(null);
    setQuotaAvailable(0);
    setCourseAssignments([]);
    onClose();
  };

  const getDialogTitle = () => {
    if (isCompanyTargetAssigner) return "Assign Course to Company";
    if (isCompany) return "Assign Course";
    if (isManagerOrHR) return hasBranch ? "Assign Course to Branch Employee" : "Assign Course to Employee or Branch";
    return "Assign Course";
  };

  const validationSchema = Yup.object({
    quota: Yup.number()
      .transform((value, originalValue) => (originalValue === "" || originalValue === null ? NaN : value))
      .when([], {
        is: () => canAssignToBranch && assignmentType === "branch" && isPaidCourse,
        then: (schema) =>
          isUnlimitedSeats(branchSeatsAvailable)
            ? schema
              .required("Seats is required")
              .integer("Seats must be a whole number")
              .moreThan(0, "Seats must be greater than 0")
            : schema
              .required("Seats is required")
              .integer("Seats must be a whole number")
              .moreThan(0, "Seats must be greater than 0")
              .max(branchSeatsAvailable, `Seats must be less than or equal to available seats (${formatSeatsLabel(branchSeatsAvailable)}).`),
        otherwise: (schema) =>
          schema
            .test("quota-min-or-unlimited", "Invalid quota", function (value) {
              if (value === undefined || value === null || Number.isNaN(value)) return true;
              if (value === -1) return true;
              const isFree = Boolean((this.parent as any).isFreeAccess);
              if (isCompanyTargetAssigner && isPaidCourse && !isFree) {
                return value >= 0;
              }
              return value >= 1;
            })
            .nullable(),
      }),
  });

  if (isRestrictedEmployee && !isManagerOrHR && !isSuperAdmin && !isCompany) {
    return <></>;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{getDialogTitle()}</DialogTitle>
      {isCompany && !existingAssignment && !loadingAssignment && assignmentType === 'employee' && (
        <Alert severity="warning" sx={{ mx: 3, mt: 1 }}>
          You have not been assigned this course yet. Only courses assigned to your company can be forwarded to employees.
        </Alert>
      )}
      <DialogContent>
        <Formik
          initialValues={{
            quota: isCompanyTargetAssigner && isPaidCourse ? 0 : 1,
            isFreeAccess: false,
            expiryDate: "",
          }}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ submitForm, values, setFieldValue }) => (
            <Form>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text" mb={2} {...({} as any)}>
                    Course: <strong>{courseTitle}</strong>
                  </Typography>

                  {isCompany && existingAssignment && existingAssignment.isFreeAccess && assignmentType === 'employee' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2" {...({} as any)}>
                        <strong>Quota Available:</strong> {formatSeatsLabel(quotaAvailable)} of {formatSeatsLabel(existingAssignment.quota)} remaining
                      </Typography>
                    </Alert>
                  )}

                  {isCompany && existingAssignment && existingAssignment.isFreeAccess && assignmentType === 'employee' && !isUnlimitedSeats(quotaAvailable) && quotaAvailable <= 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2" {...({} as any)}>
                        No quota available. All assigned quota has been used.
                      </Typography>
                    </Alert>
                  )}

                  {isManagerOrHR && hasBranch && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2" {...({} as any)}>
                        Showing employees from your branch only.
                      </Typography>
                    </Alert>
                  )}
                </Grid>

                {/* Company/manager-without-branch toggle: assign to employee or branch */}
                {canAssignToBranch && !isCompanyTargetAssigner && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text" fontWeight="bold" mb={1} display="block">
                      Assign To
                    </Typography>
                    <ToggleButtonGroup
                      value={assignmentType}
                      exclusive
                      onChange={(_, val) => { if (val) { setAssignmentType(val); setSelectedEmployee(null); setSelectedBranch(null); } }}
                      size="small"
                    >
                      <ToggleButton value="employee">Employee</ToggleButton>
                      <ToggleButton value="branch">Branch</ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>
                )}

                {isCompanyTargetAssigner && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text" fontWeight="bold" mb={0.5} display="block">
                        Select Company *
                      </Typography>
                      <Autocomplete
                        options={companies}
                        getOptionLabel={(option) => option.companyName || option.name || option.email}
                        value={selectedCompany}
                        onChange={(_, newValue) => setSelectedCompany(newValue)}
                        loading={loadingCompanies}
                        renderOption={(props, option) => {
                          const assigned = isCompanyOptionAssigned(option);
                          return (
                            <li {...props} key={option._id}>
                              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" {...({} as any)}>
                                <Typography variant="body2">{option.companyName || option.name || option.email}</Typography>
                                {assigned && (
                                  <Chip label="Already assigned" size="small" color="warning" variant="outlined" sx={{ ml: 1 }} />
                                )}
                              </Box>
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <Input
                            {...(params.InputProps as any)}
                            inputProps={params.inputProps}
                            id={params.id}
                            disabled={params.disabled}
                            fullWidth
                            placeholder="Search companies..."
                            size="large"
                          />
                        )}
                      />
                    </Grid>
                    {!hideSuperAdminCompanyControls && (!isPaidCourse || (isPaidCourse && values.isFreeAccess)) && (
                      <Grid item xs={12} md={6}>
                        <FormikInput
                          name="quota"
                          type="number"
                          label="Quota (Number of users) *"
                          placeholder="1"
                          size="large"
                        />
                        <Typography variant="caption" color="text" mt={0.5} display="block">
                          Number of users who can access this course (-1 means unlimited)
                        </Typography>
                      </Grid>
                    )}
                    {hideSuperAdminCompanyControls && (
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ mb: 1 }}>
                          <Typography variant="body2" {...({} as any)}>
                            For courses added by Creator, CompanyAdmin, or FellowAdmin, super admin assignment uses fixed defaults (no free access and no expiry date).
                          </Typography>
                        </Alert>
                      </Grid>
                    )}
                  </>
                )}

                {/* Employee assignment: for company (employee tab) or manager/hr */}
                {(isCompany || isManagerOrHR) && assignmentType === 'employee' && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text" fontWeight="bold" mb={0.5} display="block">
                        Select Employee *
                      </Typography>
                      <Autocomplete
                        options={employees}
                        getOptionLabel={(option) => option.name || option.email}
                        value={selectedEmployee}
                        onChange={(_, newValue) => setSelectedEmployee(newValue)}
                        loading={loadingEmployees}
                        getOptionDisabled={(option: Employee) =>
                          assignedEmployees.some((emp) => String(emp._id) === String(option._id))
                        }
                        renderInput={(params) => (
                          <Input
                            {...(params.InputProps as any)}
                            inputProps={params.inputProps}
                            id={params.id}
                            disabled={params.disabled}
                            fullWidth
                            placeholder="Search employees..."
                            size="large"
                          />
                        )}
                        renderOption={(props, option) => {
                          const isAssigned = assignedEmployees.some((emp) => String(emp._id) === String(option._id));
                          return (
                            <li {...props} key={option._id}>
                              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" {...({} as any)}>
                                <Typography variant="body2">{option.name || option.email}</Typography>
                                {isAssigned && (
                                  <Chip icon={<CheckCircle />} label="Already assigned" size="small" color="warning" variant="outlined" />
                                )}
                              </Box>
                            </li>
                          );
                        }}
                      />
                      <Typography variant="caption" color="text" mt={0.5} display="block">
                        Already-assigned employees are disabled; company and branch rows only show a label.
                      </Typography>
                    </Grid>
                    {assignedEmployees.length > 0 && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" mb={1}>
                          Already Assigned Employees ({assignedEmployees.length})
                        </Typography>
                        <List dense>
                          {assignedEmployees.map((emp) => (
                            <ListItem key={emp._id}>
                              <ListItemText primary={emp.name || emp.email} secondary="Course already assigned" />
                              <CheckCircle color="success" fontSize="small" />
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                    )}
                  </>
                )}

                {/* Branch assignment: for company and manager/hr without branch */}
                {canAssignToBranch && assignmentType === 'branch' && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text" fontWeight="bold" mb={0.5} display="block">
                        Select Branch *
                      </Typography>
                      <Autocomplete
                        options={branches}
                        getOptionLabel={(option) => option.name || option.email || option._id}
                        value={selectedBranch}
                        onChange={(_, newValue) => setSelectedBranch(newValue)}
                        loading={loadingBranches}
                        renderOption={(props, option) => {
                          const assigned = isBranchOptionAssigned(option);
                          return (
                            <li {...props} key={option._id}>
                              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" {...({} as any)}>
                                <Typography variant="body2">{option.name || option.email || option._id}</Typography>
                                {assigned && (
                                  <Chip label="Already assigned" size="small" color="warning" variant="outlined" sx={{ ml: 1 }} />
                                )}
                              </Box>
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <Input
                            {...(params.InputProps as any)}
                            inputProps={params.inputProps}
                            id={params.id}
                            disabled={params.disabled}
                            fullWidth
                            placeholder="Search branches..."
                            size="large"
                          />
                        )}
                      />
                      <Typography variant="caption" color="text" mt={0.5} display="block">
                        All employees of the selected branch will have access
                      </Typography>
                    </Grid>

                    {isPaidCourse && (
                      <Grid item xs={12} md={6}>
                        <FormikInput
                          name="quota"
                          type="number"
                          label="Seats to Assign *"
                          placeholder="1"
                          size="large"
                        />
                        <Typography variant="caption" color="text" mt={0.5} display="block">
                          Available seats: {formatSeatsLabel(branchSeatsAvailable)}{isUnlimitedSeats(branchSeatsAvailable) ? " (Unlimited)" : ""}
                        </Typography>
                      </Grid>
                    )}
                  </>
                )}

                {isCompanyTargetAssigner && !hideSuperAdminCompanyControls && (
                  <>
                    {isPaidCourse && !values.isFreeAccess && (
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2" {...({} as any)}>
                            <strong>Note:</strong> This is a paid course. Since &quot;Free Access&quot; is not enabled, the company will need to purchase seats to access this course.
                          </Typography>
                        </Alert>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          p: 1.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                        {...({} as any)}
                      >
                        <Box {...({} as any)}>
                          <Typography variant="body2" fontWeight="bold">
                            Free Access
                          </Typography>
                          <Typography variant="caption" color="text">
                            Allow access without a subscription requirement
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={Boolean(values.isFreeAccess)}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                void setFieldValue("isFreeAccess", checked);
                                if (!checked && isPaidCourse) {
                                  void setFieldValue("quota", 0);
                                } else if (checked && isPaidCourse) {
                                  const q = Number(values.quota);
                                  if (!Number.isFinite(q) || q < 1) {
                                    void setFieldValue("quota", 1);
                                  }
                                }
                                if (!checked) {
                                  void setFieldValue("expiryDate", "");
                                }
                              }}
                              color="info"
                            />
                          }
                          label=""
                          sx={{ mr: 0 }}
                        />
                      </Box>
                    </Grid>
                    {values.isFreeAccess && (
                      <Grid item xs={12} md={6}>
                        <FormikInput
                          name="expiryDate"
                          type="date"
                          label="Expiry Date (Optional)"
                          size="large"
                        />
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
              <DialogActions sx={{ mt: 3, px: 0 }}>
                <Button onClick={handleClose} variant="outlined" color="secondary">
                  Cancel
                </Button>
                <Button
                  onClick={submitForm}
                  variant="gradient"
                  color="info"
                  loading={loading}
                  disabled={Boolean(
                    (isCompanyTargetAssigner && !selectedCompany) ||
                    ((isCompany || isManagerOrHR) &&
                      assignmentType === 'employee' &&
                      (!selectedEmployee ||
                        assignedEmployees.some((e) => String(e._id) === String(selectedEmployee?._id)) ||
                        (hasForwardingQuotaConstraint && !isUnlimitedSeats(quotaAvailable) && quotaAvailable <= 0))) ||
                    (canAssignToBranch && assignmentType === 'branch' && !selectedBranch) ||
                    (canAssignToBranch && assignmentType === 'branch' && isPaidCourse && (
                      !values.quota ||
                      Number(values.quota) <= 0 ||
                      (!isUnlimitedSeats(branchSeatsAvailable) && Number(values.quota) > branchSeatsAvailable)
                    ))
                  )}
                >
                  Assign Course
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

export default CourseAssignmentDialog;
